import { useEffect, useState } from 'react';
import { QueryClient, QueryCache, QueryClientProvider } from 'react-query';
import { getSession, onAuthStateChange } from 'resources/auth/auth.service';
import * as routes from 'pages/routes';
// import { ReactQueryDevtools } from 'react-query/devtools';
import PropTypes from 'prop-types';
import Head from 'next/head';
import Script from 'next/script';
import { hotjar } from 'react-hotjar';
import ModalManager from '@ebay/nice-modal-react';
import SignInModal from 'modals/SignInModal';
import config from 'config';
import PageConfig from './PageConfig';
import 'styles/globals.css';

const twentyFourHoursInMs = 1000 * 60 * 60 * 24;
const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (query.state.data !== undefined) {
        // TODO: show toast from here
        console.dir(error);
      }
    },
  }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnmount: true,
      refetchOnReconnect: false,
      retry: false,
      retryOnMount: false,
      staleTime: twentyFourHoursInMs,
    },
  },
});

const App = ({ Component, pageProps }) => {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);

  useEffect(() => {
    const activeSession = getSession();
    setSession(activeSession);
    const listener = onAuthStateChange((event, updatedSession) => {
      // show Sign In modal if user has signed out
      if (event === 'SIGNED_OUT') {
        setTimeout(() => {
          if (!routes.isPublicPage(window.location.href)) {
            ModalManager.show(SignInModal);
          }
        }, 3000); // add delay to ensure the browser page url (window.location.href) is updated
      } else if (event === 'SIGNED_IN') {
        const currentUser = queryClient.getQueryData(['user.currentUser']);
        // hide Sign In modal if the same user has signed in
        if (currentUser?.email && updatedSession?.user?.email && currentUser.email === updatedSession.user.email) {
          ModalManager.hide(SignInModal);
        }
      }

      /**
      * Prevent forced redraw on tab focus:
      * onAuthStateChange (SIGNED_IN event) Fired everytime I change Chrome Tab or refocus on tab.
      * https://github.com/supabase/supabase/issues/7250
      */
      if (JSON.stringify(activeSession) !== JSON.stringify(updatedSession)) {
        setSession(updatedSession);
      }
    });

    setLoading(false);

    return () => {
      listener.unsubscribe();
    };
  }, []);

  useEffect(() => {
    try {
      hotjar.initialize(config.hotjar.id, 1);
    } catch (e) {
      console.error('Error initializing Hotjar', e);
    }
  }, []);

  const isProduction = config.env === 'production';

  if (loading) return null;

  return (
    <>
      <script async src="trackerScript.js" />
      <Head>
        <title>ListedKit</title>

        {isProduction && (
          <>
            <script>
              window.dataLayer = window.dataLayer || [];
            </script>

            <script>
              {(function (w, d, s, l, i) {
                w[l] = w[l] || []; w[l].push({
                  'gtm.start':
                    new Date().getTime(),
                  event: 'gtm.js',
                }); const f = d.getElementsByTagName(s)[0];
                const j = d.createElement(s);
                const dl = l !== 'dataLayer' ? `&l=${l}` : '';
                j.async = true;
                j.src = `https://www.googletagmanager.com/gtm.js?id=${i}${dl}`;
                f.parentNode.insertBefore(j, f);
              }(window, document, 'script', 'dataLayer', 'GTM-5CBKSMB'))}
            </script>
          </>
        )}
      </Head>

      {isProduction && (
        <noscript>
          <iframe
            title="tag-manager"
            src="https://www.googletagmanager.com/ns.html?id=GTM-5CBKSMB"
            height="0"
            width="0"
            style={{ display: 'none', visibility: 'hidden' }}
          />
        </noscript>
      )}

      <Script
        async
        src={`https://maps.googleapis.com/maps/api/js?key=${config.googleMaps.apiKey}&libraries=places&region=us`}
      />

      <Script
        async
        src="https://cdn.promotekit.com/promotekit.js"
        data-promotekit="a61ee714-a53b-4d8c-94c4-2f003be16f60"
      />

      <QueryClientProvider client={queryClient}>
        <ModalManager.Provider>
          <PageConfig session={session}>
            <Component {...pageProps} />
          </PageConfig>
        </ModalManager.Provider>
        {/* Uncomment during development, when really needed */}
        {/* <ReactQueryDevtools initialIsOpen={false} /> */}
      </QueryClientProvider>
    </>
  );
};

App.propTypes = {
  Component: PropTypes.elementType.isRequired,
  pageProps: PropTypes.shape({}).isRequired,
};

export default App;
